import React, { useMemo } from "react";
import { Box } from "@mui/material";

import { selectVersionsByProjectId } from "fond/api";
import { CTS_SOURCE, NBN_SOURCE_LAYER, NBN_SOURCE_LAYER_FILTER } from "fond/draw/modes/cts/constants";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { ImportStatus, NBNCTSInputLayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { AccordionStepper, AccordionStepperPane, StackedNavigation } from "fond/widgets";

import InputPanel from "../InputPanel/InputPanel";

import AnalysisPanel from "./AnalysisPanel";
import ExcludeDrawPanel from "./ExcludeDrawPanel";
import GeneratePanel from "./GeneratePanel";

interface CostToServePanelProps {
  // The project currently being viewed / edited
  project: Project;

  // Flag indicating if the project is read only
  readOnly: boolean;
}

const DesignPanel: React.FC<CostToServePanelProps> = ({ project, readOnly }: CostToServePanelProps) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, project.ID));
  const inputLayers = useGetImportLayers(versionId, Object.values(NBNCTSInputLayerIds));
  const { workflowState } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.NBN_COST_TO_SERVE.ROOT });

  const hasInProgressLayers = useMemo(() => {
    return Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));
  }, [inputLayers]);

  const errorLayers = useMemo(() => {
    return Array.from(inputLayers.values()).filter((layer) => layer.status === ImportStatus.ERROR);
  }, [inputLayers]);

  const allLayersUploaded = useMemo(() => {
    return Array.from(inputLayers.values()).every((layer) => layer.layer);
  }, [inputLayers]);

  const isImportComplete = allLayersUploaded && !hasInProgressLayers && errorLayers.length === 0;

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Input data",
        id: "input",
        content: (
          <InputPanel
            inProgress={hasInProgressLayers}
            errors={errorLayers}
            complete={isImportComplete}
            isReadOnly={readOnly}
            type="nbn_cost_to_serve"
          />
        ),
        isComplete: allLayersUploaded && !hasInProgressLayers && errorLayers.length === 0,
        isLoading: hasInProgressLayers,
      },
      {
        header: "Cost to serve",
        id: "connections",
        content: <GeneratePanel isReadOnly={readOnly} />,
        isComplete: workflowState === "COMPLETE",
        isLoading: false,
      },
      {
        header: "Analysis",
        id: "analysis",
        content: (
          <AnalysisPanel
            isDisabled={readOnly}
            workflowKey={WORKFLOW_KEYS.NBN_COST_TO_SERVE.ITERATION}
            isBaseDesignAvailable={workflowState === "COMPLETE"}
          />
        ),
        isComplete: (versions?.length ?? 0) > 1,
        isLoading: false,
      },
    ],
    [allLayersUploaded, errorLayers, hasInProgressLayers, isImportComplete, readOnly, versions?.length, workflowState]
  );

  const navigationScreens = [
    {
      id: "excludeDraw",
      name: "Exclude service locations",
      useDefaultHeader: false,
      component: (
        <ExcludeDrawPanel
          isReadOnly={readOnly}
          workflowKey={WORKFLOW_KEYS.NBN_COST_TO_SERVE.ITERATION}
          layerFilter={NBN_SOURCE_LAYER_FILTER}
          source={CTS_SOURCE}
          sourceLayer={NBN_SOURCE_LAYER}
        />
      ),
    },
  ];

  if (!project) return null;

  return (
    <Box data-testid="nbn-cost-to-serve-design-panel" sx={{ height: "100%" }}>
      <StackedNavigation rootComponent={<AccordionStepper id={versionId} panes={accordionPanes} />} screens={navigationScreens} />
    </Box>
  );
};

export default DesignPanel;
